import dynamic from 'next/dynamic';
import * as React from 'react';

import { Alert } from '@/core/tamagoshi/icons/Alert';
import { merchandisingLabels } from '@/core/tamagoshi/MerchandisingTag/interfaces';
import { Text } from '@/core/tamagoshi/Text/Text';
import { CashbackOfferTag } from '@/domains/productDiscovery/CommercialAnimation/components/CashbackOfferTag/CashbackOfferTag';
import { PaymentInformations } from '@/domains/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PaymentInformations/PaymentInformations';
import type { PromoBlockProps } from '@/domains/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PromoBlock/PromoBlock';
import { B2bPriceAdvantageProps } from '@/domains/productDiscovery/ProductPage/b2c/components/B2bPriceAdvantage/B2bPriceAdvantage';
import { AddToCart } from '@/domains/productDiscovery/ProductPage/common/components/AddToCart/AddToCart';
import { IndexLabels } from '@/domains/productDiscovery/ProductPage/common/components/AddToCart/IndexLabels/IndexLabels';
import { stockNotAvailable } from '@/domains/productDiscovery/ProductPage/common/components/AddToCart/translations';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useQuantity } from '@/domains/productDiscovery/ProductPage/hooks/useQuantity';
import { getTags } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';
import { useB2bPriceAdvantage } from '@/productDiscovery/ProductPage/b2b/hooks/useB2bPriceAdvantage';
import { Klarna } from '@/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PaymentInformations/Klarna';
import { BulkPurchaseTable } from '@/productDiscovery/ProductPage/common/components/BulkPurchaseTable/BulkPurchaseTable';
import { PriceAndQuantity } from '@/productDiscovery/ProductPage/common/modules/Price/PriceAndQuantity';
import { useShowStickyElements } from '@/productDiscovery/ProductPage/hooks/useShowStickyElements/useShowStickyElements';

import styles from './styles/index.default.module.scss';

const PromoBlock = dynamic<PromoBlockProps>(() =>
  import(
    '@/domains/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PromoBlock/PromoBlock'
  ).then((mod) => mod.PromoBlock),
);

const B2bPriceAdvantage = dynamic<B2bPriceAdvantageProps>(() =>
  import(
    '@/domains/productDiscovery/ProductPage/b2c/components/B2bPriceAdvantage/B2bPriceAdvantage'
  ).then((mod) => mod.B2bPriceAdvantage),
);

const DynamicCoupon = dynamic(() =>
  import('@/domains/loyalty/components/Coupon/ProductCoupon').then(
    (mod) => mod.ProductCoupon,
  ),
);

const MIN_PERCENT_ADVANTAGE = 2;

export const AddToCartBlock: React.FC = () => {
  const quantity = useQuantity();
  const detail = useDetail<'b2c'>();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { showStickyA2C } = useShowStickyElements(ref);
  const b2bPriceAdvantage = useB2bPriceAdvantage(detail?.modelId) ?? 0;

  const { price, productId, commercialAnimation } = detail;

  const hasB2bPriceAdvantage = b2bPriceAdvantage >= MIN_PERCENT_ADVANTAGE;

  const promoTags = getTags(
    detail.price?.primaryPrice.amountWithVat,
    detail.price?.retailPrice?.amountWithVat ?? 0,
    commercialAnimation?.tags,
  );
  const withBlackFriday = promoTags.includes(merchandisingLabels.BLACKFRIDAY);
  const withSales = promoTags.includes(merchandisingLabels.SALES);
  const withManoManoDays = promoTags.includes(merchandisingLabels.MANOMANODAYS);
  const withLoweredPrice = promoTags.includes(
    merchandisingLabels.LOWERED_PRICE,
  );

  let promoType = merchandisingLabels.PROMO;
  if (withBlackFriday) promoType = merchandisingLabels.BLACKFRIDAY;
  if (withSales) promoType = merchandisingLabels.SALES;
  if (withManoManoDays) promoType = merchandisingLabels.MANOMANODAYS;
  if (withLoweredPrice) promoType = merchandisingLabels.LOWERED_PRICE;

  return (
    <>
      {hasB2bPriceAdvantage && (
        <B2bPriceAdvantage
          articleId={detail.articleId}
          modelId={detail.modelId}
          advantagePercentage={b2bPriceAdvantage}
        />
      )}
      <div
        className={styles.addToCartContainer}
        data-abtasty-id="product-page-add-to-cart-block"
      >
        {productId && (
          <CashbackOfferTag
            className={styles.cashbackTag}
            productId={productId.toString()}
          />
        )}
        {promoTags.length > 0 && detail.commercialAnimation && (
          <PromoBlock
            promoType={promoType}
            retailPriceTimeLeftLabel={
              detail.commercialAnimation.retailPriceTimeLeftLabel || undefined
            }
          />
        )}

        <PriceAndQuantity />
        <Klarna />

        <BulkPurchaseTable />

        <IndexLabels />

        {detail.isSellable && (
          <div data-testid="coupon-wrapper" className={styles.coupon}>
            <DynamicCoupon
              context="product page"
              productId={productId ?? 0}
              quantity={quantity}
              unitPrice={price?.primaryPrice.amountWithVat || 0}
            />
          </div>
        )}

        {!detail.isSellable && (
          <Text className={styles.outOfStock}>
            <Alert className={styles.outOfStockIcon} /> {stockNotAvailable}
          </Text>
        )}

        <div className={styles.addToCart} ref={ref}>
          <AddToCart b2bPriceAdvantage={b2bPriceAdvantage} isRedesign />
        </div>

        {showStickyA2C && (
          <div data-testid="sticky-A2C-button">
            <AddToCart
              b2bPriceAdvantage={b2bPriceAdvantage}
              isSticky
              isRedesign
            />
          </div>
        )}
        <PaymentInformations />
      </div>
    </>
  );
};

AddToCartBlock.displayName = 'AddToCartBlock';
